import axios from "../general/HttpClient";
import * as AccountStore from "../account/AccountStore";

export function SearchAllUsers(query, skip, limit) {
    return axios.get("/api/users/searchAllUsers", {
        params: {
            query,
            skip,
            limit,
        },
    });
}

export function SearchUsers(query, skip, limit, includeStaff, includeStudents, includeParents, includeGuests, delegated = false) {
    return axios.get("/api/users/searchUsers", {
        params: {
            query,
            skip,
            limit,
            includeStaff,
            includeStudents,
            includeParents,
            includeGuests,
            delegated,
        },
    });
}

export function GetUser(userId) {
    return axios.get("/api/users/getUser", {
        params: {
            userId,
        },
    });
}

export function GetUserClaims(userId) {
    return axios.get("/api/users/getUserClaims", {
        params: {
            userId,
        },
    });
}

export function ResolveUsers(users) {
    return axios.post("/api/users/$resolve", { users });
}

export function SearchUsersByMetadata(metadataKey: string, metadataValue: string) {
    return axios.get("/api/users/searchUsersByMetadata", {
        params: {
            metadataKey,
            metadataValue,
        },
    });
}

export function CreateUser(request: CreateIdentityUserRequest) {
    return axios.post("/api/users/createUser", request);
}

export function UpdateUser(request: UserUpdateModel) {
    return axios.post("/api/users/UpdateUser", request);
}

export interface UserUpdateModel {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    twoFactorEnabled: boolean;
}

export function AddUserToOrganization(userId, organizationId, membershipType, source, sourceID) {
    return axios.post(
        "/api/users/addUserToOrganization",
        {},
        {
            params: {
                userId,
                organizationId,
                membershipType,
                source,
                sourceID,
            },
        }
    );
}

export function RemoveUserFromOrganization(userId, organizationId, membershipType) {
    return axios.delete("/api/users/removeUserFromOrganization", {
        params: {
            userId,
            organizationId,
            membershipType,
        },
    });
}

export function ArchiveUser(userId: string) {
    return axios.post(`/api/users/${encodeURIComponent(userId)}/archive`);
}

export function DeleteUserFromOrganization(userId: string) {
    return axios.delete(`/api/users/${encodeURIComponent(userId)}/organization-memberships`);
}

export const ImportUsersFromExcel = (organizationId, excelFileData, ignoreExistingUserUpdates = true) => {
    return axios.post(
        `api/users/importUsersFromExcel`,
        { excelFileData, ignoreExistingUserUpdates },
        {
            params: {
                organizationId,
            },
        }
    );
};

export const ImportUsersFromExcelTestRun = (organizationId, excelFileData, ignoreExistingUserUpdates = true) => {
    return axios.post(
        `api/users/importUsersFromExcelTestRun`,
        { excelFileData, ignoreExistingUserUpdates },
        {
            params: {
                organizationId,
            },
        }
    );
};

export const ResetPassword = (user, password) => {
    return axios.post(`api/users/resetPassword`, { userID: user.id, password });
};

export const GetAdmins = () => {
    return axios.get(`api/users/$admins`);
};

export const AddClaim = (userID, type, value) => {
    return axios.post(`api/users/claims`, { userID: userID, claimType: type, claimValue: value });
};

export const RemoveClaim = (userId, claimType, claimValue) => {
    return axios.delete(`api/users/claims`, { params: { userId, claimType, claimValue } });
};

export const GetUserDelegatedAccess = (userId: string) => {
    return axios.get(`/api/users/${userId}/delegatedAccess`);
};

export const AddDelegatedAccess = (userId: string, delegateeId: string, delegationRules?: string) => {
    return axios.post(`/api/users/${userId}/delegatedAccess`, {
        delegateeId,
        delegationRules
    });
};

export const RemoveDelegatedAccess = (userId: string, delegateeId: string) => {
    return axios.delete(`/api/users/${userId}/delegatedAccess/${delegateeId}`);
};

export class Organization {
    id: string;
    name: string;
    scadsysOnlineSchoolID: string;
    defaultDomain: string;
    logoUri: string;

    constructor() {
        this.id = "";
        this.name = "";
        this.scadsysOnlineSchoolID = "";
        this.defaultDomain = "";
        this.logoUri = "";
    }
}

export class OrganizationMembership {
    userID: string;
    organizationID: string;
    type: number;
    enabled: boolean;
    source: string;
    sourceID: string;
    user?: AccountStore.Account;
    organization?: Organization;

    constructor() {
        this.userID = "";
        this.organizationID = "";
        this.type = 0;
        this.enabled = true;
        this.source = "";
        this.sourceID = "";
    }
}

export interface IdentityUser {
    organization: string;
    id: string;
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    photo: string;
    hasGivenAccessTo: DelegatedAccess[];
    groups: any[];
    membershipType: any;
    sourceIdentifiers: SourceIdentifier[];
}

export interface DelegatedAccess {
    delegatorID: string;
    delegateeID: string;
    delegationRules: string;
}

export interface DelegatedAccessResponse {
    hasGivenAccessTo: DelegatedAccessGivenResponse[];
    hasAccessOn: DelegatedAccessReceivedResponse[];
}

export interface DelegatedAccessGivenResponse {
    delegatorID: string;
    delegateeID: string;
    delegationRules: string;
    delegatee: DelegatedAccessUserResponse;
}

export interface DelegatedAccessReceivedResponse {
    delegatorID: string;
    delegateeID: string;
    delegationRules: string;
    delegator: DelegatedAccessUserResponse;
}

export interface DelegatedAccessUserResponse {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    displayName: string;
}

export interface AddDelegatedAccessResponse {
    success: boolean;
    message: string;
}

export interface RemoveDelegatedAccessResponse {
    success: boolean;
    message: string;
}

export interface SourceIdentifier {
    source: string;
    sourceID: string;
}

export interface CreateIdentityUserRequest {
    id?: string;
    userName?: string;
    email: string;
    firstName?: string;
    lastName?: string;
    membershipType: number;
    sourceID?: string;
    source?: string;
    organization: string;
    photo?: string;
    phoneNumber?: string;
}
